/**
 * Client-side mirror of backend additionalField validation constants.
 *
 * Single source of truth: the Java file at
 *   backend/src/main/java/org/nikita/app/additional_field/AdditionalFieldConstraints.java
 *
 * Any rule change MUST update BOTH files + the i18n error messages in
 * frontend/messages/{locale}/errors.json. A future CI lint may grep the
 * two files for drift; for now keep them in sync by hand.
 *
 * Used by:
 *   - PAdditionalFieldSetting.tsx (admin-side guest-form schema config)
 *   - (TODO) guest-registration form for client-side block before POST
 *   - Event settings UI for event.additionalFields key validation
 */
import { DataType } from "@/types/enums/DataType";
import type { Field } from "@/types/Field";

export const ADDITIONAL_FIELD_KEY_PATTERN: RegExp =
  /^[a-zA-Z0-9_-]{1,100}$/;

/** Case-insensitive compare — call `.toLowerCase()` before membership test.
 *  MUST stay in sync with backend AdditionalFieldConstraints.RESERVED_KEYS —
 *  the auto-generated identifier dedupes against this set, so a missing key here
 *  lets the client mint an identifier the backend then 400s on create. */
export const ADDITIONAL_FIELD_RESERVED_KEYS: ReadonlySet<string> = new Set([
  "id",
  "_id",
  "eventid",
  "companyid",
  "guestid",
  "userid",
  "createdat",
  "updatedat",
  "firstname",
  "lastname",
  "email",
  "phone",
  "archived",
  "additionalfields",
  "checkedin",
  "acceptancestate",
  "role",
  "password",
  "secret",
  // Server-set consent provenance (RCS-1 port): a custom field of that name
  // would shadow the column the public form can never write.
  "emailoptinat",
  "emailoptinsource",
  // SMS channel wave: the same server-set pair for smsOptIn (projection-only on
  // the backend; smsOptIn itself is a column name, refused as such). A custom
  // key of that name would also read as the column to the backend's
  // UPDATE_GUEST consent rule. MIRROR: main-backend
  // AdditionalFieldConstraints.RESERVED_KEYS.
  "smsoptinat",
  "smsoptinsource",
  // GDPR Art. 9 / special-category data — blocked as key names outright so the
  // schema can't accidentally collect them as free-form values.
  "religion",
  "diagnosis",
  "ethnicity",
  "medical",
  "biometric",
  "health",
  "sexualorientation",
  "politicalopinion",
  "tradeunion",
  "genetic",
]);

/** Layer A — max keys on event.additionalFields (event metadata map). */
export const EVENT_ADDITIONAL_FIELD_COUNT_CAP = 20;

/** Layer B — max AdditionalField rows per event (guest-form schema). */
export const GUEST_FORM_FIELD_COUNT_CAP = 50;

export const STRING_LENGTH_GLOBAL_MAX = 2000;

export const NUMERIC_GLOBAL_BOUND = 1e15;

export const OPTIONS_COUNT_MIN = 1;
export const OPTIONS_COUNT_MAX = 100;

export const OPTION_LENGTH_MIN = 1;
export const OPTION_LENGTH_MAX = 100;

/**
 * Validate a candidate key name, returning the matching backend error code
 * or `null` if the name is acceptable. Mirrors
 * {@code EventService.validateAdditionalFieldKey} +
 * {@code AdditionalFieldValidator.validate}'s first two gates.
 */
export function validateAdditionalFieldKey(
  key: string | null | undefined,
): "invalidAdditionalFieldKey" | "reservedAdditionalFieldKey" | null {
  if (!key || !ADDITIONAL_FIELD_KEY_PATTERN.test(key)) {
    return "invalidAdditionalFieldKey";
  }
  if (ADDITIONAL_FIELD_RESERVED_KEYS.has(key.toLowerCase())) {
    return "reservedAdditionalFieldKey";
  }
  return null;
}

/**
 * Normalize a free-text display name into a charset-safe identifier that
 * matches {@link ADDITIONAL_FIELD_KEY_PATTERN} (`[a-zA-Z0-9_-]`). This is the
 * value stored as `Field.name` — the IMMUTABLE token that email / document /
 * website templates reference as `{{additionalFields.<name>}}` and that guest
 * value maps key on. The human label (spaces / umlauts / punctuation) lives on
 * `Field.translation` instead.
 *
 * Strips diacritics (ä→a, é→e), expands ß→ss, collapses every other run of
 * non-alphanumerics to a single underscore, trims underscores, lowercases, and
 * caps at 100 chars. Returns "" when the input has no usable alphanumerics
 * (caller should block create + prompt for a proper name).
 */
export function slugifyAdditionalFieldName(input: string): string {
  return (input ?? "")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // drop combining diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_") // any run of other chars → single underscore
    .replace(/^_+|_+$/g, "") // trim leading/trailing underscores
    .slice(0, 100);
}

/**
 * Derive a UNIQUE identifier from a display name: {@link slugifyAdditionalFieldName}
 * plus a numeric suffix (`_2`, `_3`, …) when the base collides with an existing
 * field name (case-insensitive) or a reserved key. `existingNames` should be the
 * event's current custom-field `name`s. Falls back to `"field"` when the name
 * slugifies to nothing, so the result is always a valid key.
 */
export function uniqueAdditionalFieldIdentifier(
  displayName: string,
  existingNames: readonly string[],
): string {
  const base = slugifyAdditionalFieldName(displayName) || "field";
  const taken = new Set(existingNames.map((n) => n.toLowerCase()));
  const isFree = (s: string): boolean =>
    !taken.has(s) && !ADDITIONAL_FIELD_RESERVED_KEYS.has(s);
  if (isFree(base)) return base;
  for (let i = 2; i < 10000; i++) {
    const candidate = `${base.slice(0, 100 - (String(i).length + 1))}_${i}`;
    if (isFree(candidate)) return candidate;
  }
  return base; // pathological fallback; backend collision check is the backstop
}

/**
 * Error codes backend emits for additionalField failures. Kept here so
 * callers can exhaustively switch + get compiler help on new codes.
 */
export type AdditionalFieldErrorCode =
  | "additionalFieldsOverCap"
  | "invalidAdditionalFieldKey"
  | "additionalFieldKeyCollision"
  | "reservedAdditionalFieldKey"
  | "invalidAdditionalFieldConstraints"
  | "additionalFieldsValidationFailed"
  | "unknownAdditionalFieldKey"
  | "invalidAdditionalFieldValueType"
  | "additionalFieldValueOutOfRange"
  | "invalidAdditionalFieldOption"
  | "requiredAdditionalFieldMissing"
  | "additionalFieldValueNotFinite"
  | "additionalFieldValueMalformed"
  | "additionalFieldSelectionCountOutOfRange"
  | "additionalFieldCountOverCap"
  // A COMPUTED field was submitted as a value. Every cockpit surface hides it,
  // so this arrives from an API caller or from a stale tab — and it must read
  // as "this column calculates itself", not as a generic failure.
  | "computedFieldNotWritable";

export type AdditionalFieldViolation = {
  fieldKey: string;
  code: AdditionalFieldErrorCode;
};

/** 400 body shape for guest-submission failures. */
export type AdditionalFieldValidationResponse = {
  errorCode: "additionalFieldsValidationFailed";
  violations: AdditionalFieldViolation[];
  violationsTruncated: boolean;
};

// =====================================================================
// Client-side guest-submission validator.
//
// Mirrors backend `GuestAdditionalFieldValidator` (plan §6). Call before
// POST to surface per-field errors client-side — but the backend remains
// the source of truth; a successful client-validate is NOT sufficient to
// skip handling the 400 response shape above.
//
// Usage:
//   const { violations } = validateSubmissionClientSide(submittedMap, schema);
//   if (violations.length) showInlineErrors(violations);
//   else await postGuest(...);
// =====================================================================

/**
 * Minimal shape of an AdditionalField row needed to validate. Matches the
 * backend model fields that affect submission validation; FE types can
 * extend this with display-only fields (translation, editable, etc.).
 */
export type AdditionalFieldSchema = {
  name: string;
  dataType:
    | "STRING"
    | "LONG"
    | "DOUBLE"
    | "BOOLEAN"
    | "EMAIL"
    | "URL"
    | "DATE"
    | "DATE_TIME"
    | "SINGLE_SELECT"
    | "MULTI_SELECT"
    | "IMAGE";
  required?: boolean | null;
  options?: string[] | null;
  lowerBound?: string | null;
  upperBound?: string | null;
  /**
   * Configured default applied on guest CREATE by the backend
   * (GuestService.processGuestsBatch → applyAdditionalFieldDefaults). A
   * required field carrying a non-blank default is never "missing" — it is
   * auto-filled — so the required-missing check below skips it, mirroring
   * GuestAdditionalFieldValidator.
   */
  defaultValue?: unknown;
};

export type ClientValidationResult = {
  violations: AdditionalFieldViolation[];
  truncated: boolean;
};

const VIOLATIONS_CAP = 200;

/**
 * Run every rule the backend runs, in the same order, against one submitted
 * map. Never throws — returns a list of violations. Use this BEFORE POSTing
 * so the guest can correct every bad field in one pass.
 */
export function validateSubmissionClientSide(
  submitted: Record<string, unknown> | null | undefined,
  schema: readonly AdditionalFieldSchema[],
): ClientValidationResult {
  const violations: AdditionalFieldViolation[] = [];
  let truncated = false;
  const byLower = new Map<string, AdditionalFieldSchema>();
  for (const f of schema) byLower.set(f.name.toLowerCase(), f);
  const safe = submitted ?? {};

  const push = (v: AdditionalFieldViolation): boolean => {
    if (violations.length >= VIOLATIONS_CAP) {
      truncated = true;
      return false;
    }
    violations.push(v);
    return true;
  };

  // (a) unknown keys
  for (const k of Object.keys(safe)) {
    if (!byLower.has(k.toLowerCase())) {
      if (!push({ fieldKey: k, code: "unknownAdditionalFieldKey" })) break;
    }
  }

  // (b) required-missing
  if (violations.length < VIOLATIONS_CAP) {
    for (const f of schema) {
      if (!f.required) continue;
      // A required field with a non-blank default is auto-filled on create —
      // never report it missing (mirrors GuestAdditionalFieldValidator).
      if (!isBlank(f.defaultValue)) continue;
      const v = lookupCaseInsensitive(safe, f.name);
      if (isBlank(v)) {
        if (!push({ fieldKey: f.name, code: "requiredAdditionalFieldMissing" })) break;
      }
    }
  }

  // (c) per-field type/range/options
  for (const [k, raw] of Object.entries(safe)) {
    if (violations.length >= VIOLATIONS_CAP) {
      truncated = true;
      break;
    }
    const f = byLower.get(k.toLowerCase());
    if (!f) continue; // already reported as unknown
    if (isBlank(raw)) continue; // required-missing already reported
    const code = checkValue(f, raw);
    if (code) push({ fieldKey: f.name, code });
  }

  return { violations, truncated };
}

function lookupCaseInsensitive(obj: Record<string, unknown>, name: string): unknown {
  if (name in obj) return obj[name];
  const lc = name.toLowerCase();
  for (const [k, v] of Object.entries(obj)) if (k.toLowerCase() === lc) return v;
  return undefined;
}

function isBlank(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function checkValue(
  f: AdditionalFieldSchema,
  raw: unknown,
): AdditionalFieldErrorCode | null {
  const lo = f.lowerBound != null ? Number(f.lowerBound) : null;
  const hi = f.upperBound != null ? Number(f.upperBound) : null;
  const globalCap = NUMERIC_GLOBAL_BOUND;

  switch (f.dataType) {
    case "STRING":
    case "EMAIL": {
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      const trimmed = raw.trim();
      const effectiveLo = lo != null && Number.isFinite(lo) ? lo : 0;
      const effectiveHi =
        hi != null && Number.isFinite(hi) ? hi : STRING_LENGTH_GLOBAL_MAX;
      if (trimmed.length < effectiveLo || trimmed.length > effectiveHi)
        return "additionalFieldValueOutOfRange";
      if (f.dataType === "EMAIL" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed))
        return "additionalFieldValueMalformed";
      if (f.options && !f.options.includes(trimmed))
        return "invalidAdditionalFieldOption";
      return null;
    }
    case "URL": {
      // URL fields have no configurable bounds; just a format + global-cap
      // check. Regexes mirror urlField.ts and the Java AdditionalFieldConstraints.
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      const trimmed = raw.trim();
      if (trimmed.length > STRING_LENGTH_GLOBAL_MAX)
        return "additionalFieldValueOutOfRange";
      // Requires a host with a dot + 2+ letter TLD — mirrors urlField.ts
      // URL_CORE and the Java AdditionalFieldConstraints.
      const core =
        "https?:\\/\\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}(?::\\d+)?(?:[/?#][^\\s)]*)?";
      const bare = new RegExp(`^${core}$`, "i");
      const md = new RegExp(`^\\[[^\\]]*\\]\\((?:${core})\\)$`, "i");
      if (trimmed !== "" && !bare.test(trimmed) && !md.test(trimmed))
        return "additionalFieldValueMalformed";
      return null;
    }
    case "LONG": {
      const n = coerceNumber(raw);
      if (n == null || !Number.isFinite(n) || !Number.isInteger(n))
        return "invalidAdditionalFieldValueType";
      if (Math.abs(n) > globalCap) return "additionalFieldValueOutOfRange";
      if (lo != null && n < lo) return "additionalFieldValueOutOfRange";
      if (hi != null && n > hi) return "additionalFieldValueOutOfRange";
      return null;
    }
    case "DOUBLE": {
      const n = coerceNumber(raw);
      if (n == null) return "invalidAdditionalFieldValueType";
      if (!Number.isFinite(n)) return "additionalFieldValueNotFinite";
      if (Math.abs(n) > globalCap) return "additionalFieldValueOutOfRange";
      if (lo != null && n < lo) return "additionalFieldValueOutOfRange";
      if (hi != null && n > hi) return "additionalFieldValueOutOfRange";
      return null;
    }
    case "BOOLEAN":
      return typeof raw === "boolean" ? null : "invalidAdditionalFieldValueType";
    case "DATE":
    case "DATE_TIME": {
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      // Strip a trailing IANA zone-region suffix (e.g. "…Z[UTC]") before
      // Date.parse — JSON-B/Yasson can emit it for java.util.Date and
      // Date.parse returns NaN on it, which would mis-flag valid dates.
      const stripZone = (s: string) => s.trim().replace(/\[[^\]]*\]$/, "");
      const t = Date.parse(stripZone(raw));
      if (!Number.isFinite(t)) return "additionalFieldValueMalformed";
      const loT = f.lowerBound ? Date.parse(stripZone(f.lowerBound)) : null;
      const hiT = f.upperBound ? Date.parse(stripZone(f.upperBound)) : null;
      if (loT != null && Number.isFinite(loT) && t < loT)
        return "additionalFieldValueOutOfRange";
      if (hiT != null && Number.isFinite(hiT) && t > hiT)
        return "additionalFieldValueOutOfRange";
      return null;
    }
    case "SINGLE_SELECT": {
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      if (f.options && !f.options.includes(raw.trim()))
        return "invalidAdditionalFieldOption";
      return null;
    }
    case "IMAGE": {
      // IMAGE has no bounds/options and is never typed by hand — the only
      // acceptable non-blank value is the backend-minted
      // {key, contentType, size} object (echoed deep-equal on save; the
      // presign→confirm flow is the sole writer). Anything else is rejected,
      // mirroring the backend's fail-closed IMAGE arm.
      if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return "invalidAdditionalFieldValueType";
      }
      const v = raw as Record<string, unknown>;
      const sizeOk =
        typeof v.size === "number" &&
        v.size > 0 &&
        v.size <= 10 * 1024 * 1024;
      const typeOk =
        typeof v.contentType === "string" &&
        ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(
          v.contentType.toLowerCase(),
        );
      const keyOk =
        typeof v.key === "string" && v.key.startsWith("af-images/");
      return keyOk && typeOk && sizeOk
        ? null
        : "invalidAdditionalFieldValueType";
    }
    case "MULTI_SELECT": {
      if (!Array.isArray(raw)) return "invalidAdditionalFieldValueType";
      for (const v of raw) {
        if (typeof v !== "string") return "invalidAdditionalFieldValueType";
        if (f.options && !f.options.includes(v.trim()))
          return "invalidAdditionalFieldOption";
      }
      // For MULTI_SELECT the bounds are a SELECTION COUNT (min/max # of options
      // a guest may pick), not a value/length. Enforce them here so the count
      // limit is blocked client-side before POST, mirroring the backend.
      if (lo != null && raw.length < lo)
        return "additionalFieldSelectionCountOutOfRange";
      if (hi != null && raw.length > hi)
        return "additionalFieldSelectionCountOutOfRange";
      return null;
    }
  }
}

function coerceNumber(raw: unknown): number | null {
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isNaN(n) && t.toLowerCase() !== "nan" ? null : n;
  }
  return null;
}

/**
 * Live editor-side check: does a configured DEFAULT value fall inside the
 * field's min/max bounds? Reuses {@link checkValue}'s range logic so the admin
 * sees a specific inline error the moment the default drifts out of range,
 * instead of only after a Save round-trip. Returns the matching error code or
 * `null` when the default is acceptable (or the type carries no range here).
 *
 * Scope: STRING (length), LONG, DOUBLE, CURRENCY, DATE, DATE_TIME. BOOLEAN /
 * URL / EMAIL / SELECT return `null` — no bounds, or option/format validity is
 * left to the backend to avoid false positives while the field is mid-edit.
 */
export function validateDefaultAgainstBounds(args: {
  dataType: DataType | null;
  defaultValue: unknown;
  lowerBound: number | Date | string | null | undefined;
  upperBound: number | Date | string | null | undefined;
  options?: string[] | null;
}): AdditionalFieldErrorCode | null {
  const { dataType, defaultValue, lowerBound, upperBound, options } = args;
  if (dataType == null || isBlank(defaultValue)) return null;

  // IMAGE fields must not carry ANY default (backend rejects a non-blank one;
  // the value is only settable per guest via the upload flow). The editor
  // hides the default input for IMAGE, so a non-blank default here is a
  // legacy/stray value — flag it instead of silently passing.
  if (dataType === DataType.IMAGE) return "invalidAdditionalFieldValueType";

  if (
    dataType === DataType.BOOLEAN ||
    dataType === DataType.URL ||
    dataType === DataType.EMAIL ||
    dataType === DataType.SINGLE_SELECT ||
    dataType === DataType.MULTI_SELECT
  ) {
    return null;
  }

  // CURRENCY reuses the DOUBLE numeric-range path (checkValue has no CURRENCY
  // case); everything else maps 1:1 to the schema dataType union.
  const schemaType: AdditionalFieldSchema["dataType"] =
    dataType === DataType.CURRENCY
      ? "DOUBLE"
      : (dataType as AdditionalFieldSchema["dataType"]);

  // Bounds → the string shape checkValue expects. NEVER Date-wrap ISO date
  // strings (tz double-offset); checkValue's DATE branch Date.parses them raw.
  const toBound = (
    b: number | Date | string | null | undefined,
  ): string | null => {
    if (b == null) return null;
    if (b instanceof Date) return b.toISOString();
    if (typeof b === "number") return String(b);
    return b;
  };

  const schema: AdditionalFieldSchema = {
    name: "",
    dataType: schemaType,
    // Empty options ([]) is truthy and would make checkValue's STRING branch
    // reject every default as an invalid option — normalize to undefined.
    options: options && options.length > 0 ? options : undefined,
    lowerBound: toBound(lowerBound),
    upperBound: toBound(upperBound),
  };

  return checkValue(schema, defaultValue);
}

// =====================================================================
// HTML5 attribute derivation — use on <input> to pre-empt bad input UX.
// =====================================================================

export type InputAttrs = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: string | number;
  max?: string | number;
  type?: "text" | "email" | "number" | "date" | "datetime-local";
};

/**
 * Translate an {@link AdditionalFieldSchema} into HTML5 input attributes.
 * Pair with {@link validateSubmissionClientSide} for a submit-time pass.
 */
export function inputAttrsFromSchema(f: AdditionalFieldSchema): InputAttrs {
  const out: InputAttrs = { required: !!f.required };
  switch (f.dataType) {
    case "STRING":
    case "EMAIL":
      out.type = f.dataType === "EMAIL" ? "email" : "text";
      if (f.lowerBound != null) {
        const n = Number(f.lowerBound);
        if (Number.isFinite(n)) out.minLength = n;
      }
      if (f.upperBound != null) {
        const n = Number(f.upperBound);
        if (Number.isFinite(n)) out.maxLength = n;
      }
      break;
    case "LONG":
    case "DOUBLE":
      out.type = "number";
      if (f.lowerBound != null) out.min = f.lowerBound;
      if (f.upperBound != null) out.max = f.upperBound;
      break;
    case "DATE":
      out.type = "date";
      if (f.lowerBound) out.min = f.lowerBound;
      if (f.upperBound) out.max = f.upperBound;
      break;
    case "DATE_TIME":
      out.type = "datetime-local";
      if (f.lowerBound) out.min = f.lowerBound;
      if (f.upperBound) out.max = f.upperBound;
      break;
    case "BOOLEAN":
    case "SINGLE_SELECT":
    case "MULTI_SELECT":
    case "IMAGE":
      // Handled as <input type=checkbox> / <select> / checkbox-group / upload
      // widget — no min/max attrs apply. Caller renders the appropriate control.
      break;
  }
  return out;
}

// =====================================================================
// i18n key mapping. Caller wires to their i18n runtime; this just gives
// a stable key per backend code so translations can be looked up.
// =====================================================================

// =====================================================================
// Human-readable, BOUND-AWARE violation messages.
//
// The backend returns per-field violations as `{ fieldKey, code }` — a bare
// code like `additionalFieldValueOutOfRange` reads the same for a length limit,
// a numeric ceiling or a date window, and never tells the admin WHAT the limit
// is. `describeAdditionalFieldViolation` turns one violation + the field's
// schema into a specific sentence ("„Preis" darf höchstens 500 sein."), so the
// admin can fix it without guessing. Falls back to the generic per-code text
// when the bound isn't known (e.g. only the global cap was hit).
// =====================================================================

/** next-intl translator, narrowed to the errors namespace we call with. */
type ErrorsT = (key: string, values?: Record<string, string | number>) => string;

function toFiniteNumber(b: number | Date | string | null | undefined): number | null {
  if (b == null) return null;
  if (typeof b === "number") return Number.isFinite(b) ? b : null;
  if (b instanceof Date) return null; // numeric bounds are never Date-wrapped
  const n = Number(b);
  return Number.isFinite(n) ? n : null;
}

/**
 * True when a field's lower bound is strictly greater than its upper bound.
 * Numeric / selection-count bounds are compared AS NUMBERS — a plain `>` on the
 * stored values compares them as strings (`"2" > "10"` is `true`), which
 * false-rejects any count/number ≥ 10. DATE / DATE_TIME bounds are ISO-UTC
 * strings whose lexical order already matches chronological order, so they fall
 * back to a string compare. Null/undefined on either side ⇒ not out of order.
 */
export function boundsInWrongOrder(
  lower: number | Date | string | null | undefined,
  upper: number | Date | string | null | undefined,
): boolean {
  if (lower == null || upper == null) return false;
  const lo = toFiniteNumber(lower);
  const hi = toFiniteNumber(upper);
  if (lo != null && hi != null) return lo > hi;
  return String(lower) > String(upper);
}

/** Format a numeric bound; money fields render with their currency symbol. */
function formatBoundNumber(field: Pick<Field, "dataType" | "currencyCode">, n: number, locale: string): string {
  if (field.dataType === DataType.CURRENCY && field.currencyCode) {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: field.currencyCode,
      }).format(n);
    } catch {
      // Unknown/invalid ISO code — fall through to a plain number.
    }
  }
  return n.toLocaleString(locale);
}

/** Format a DATE / DATE_TIME bound. UTC-framed to match the stored instant so
 *  the message never shows a tz-shifted day (mirrors the DATE-only handling in
 *  the pickers). */
function formatBoundDate(iso: string, withTime: boolean, locale: string): string {
  const ms = Date.parse(iso.trim().replace(/\[[^\]]*\]$/, ""));
  if (!Number.isFinite(ms)) return iso;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    timeZone: "UTC",
  }).format(new Date(ms));
}

export function describeAdditionalFieldViolation(
  field: Pick<
    Field,
    "name" | "dataType" | "lowerBound" | "upperBound" | "currencyCode"
  >,
  code: AdditionalFieldErrorCode,
  t: ErrorsT,
  locale: string,
): string {
  const name = field.name;
  const dt = field.dataType;
  const isNumeric =
    dt === DataType.LONG || dt === DataType.DOUBLE || dt === DataType.CURRENCY;
  const isText = dt === DataType.STRING || dt === DataType.EMAIL;
  const isDate = dt === DataType.DATE || dt === DataType.DATE_TIME;

  switch (code) {
    case "requiredAdditionalFieldMissing":
      return t("afvRequired", { field: name });

    case "additionalFieldValueOutOfRange": {
      if (isText) {
        // STRING / EMAIL bounds are character-length limits.
        const lo = toFiniteNumber(field.lowerBound);
        const hi = toFiniteNumber(field.upperBound);
        if (lo != null && hi != null)
          return t("afvLengthRange", { field: name, min: lo, max: hi });
        if (hi != null) return t("afvLengthMax", { field: name, max: hi });
        if (lo != null) return t("afvLengthMin", { field: name, min: lo });
      } else if (isNumeric) {
        const lo = toFiniteNumber(field.lowerBound);
        const hi = toFiniteNumber(field.upperBound);
        if (lo != null && hi != null)
          return t("afvNumberRange", {
            field: name,
            min: formatBoundNumber(field, lo, locale),
            max: formatBoundNumber(field, hi, locale),
          });
        if (hi != null)
          return t("afvNumberMax", { field: name, max: formatBoundNumber(field, hi, locale) });
        if (lo != null)
          return t("afvNumberMin", { field: name, min: formatBoundNumber(field, lo, locale) });
      } else if (isDate) {
        const withTime = dt === DataType.DATE_TIME;
        const lo =
          typeof field.lowerBound === "string" && field.lowerBound
            ? formatBoundDate(field.lowerBound, withTime, locale)
            : null;
        const hi =
          typeof field.upperBound === "string" && field.upperBound
            ? formatBoundDate(field.upperBound, withTime, locale)
            : null;
        if (lo != null && hi != null)
          return t("afvDateRange", { field: name, min: lo, max: hi });
        if (hi != null) return t("afvDateMax", { field: name, max: hi });
        if (lo != null) return t("afvDateMin", { field: name, min: lo });
      }
      // No configured bound (global cap hit) — no number to show.
      return t("afvGeneric", { field: name });
    }

    case "additionalFieldSelectionCountOutOfRange": {
      // MULTI_SELECT count limit — read the bounds as counts (integers).
      const min = toFiniteNumber(field.lowerBound);
      const max = toFiniteNumber(field.upperBound);
      if (min != null && max != null)
        return t("afvSelectionRange", { field: name, min, max });
      if (max != null) return t("afvSelectionMax", { field: name, max });
      if (min != null) return t("afvSelectionMin", { field: name, min });
      return t("afvGeneric", { field: name });
    }

    case "additionalFieldValueMalformed":
      if (dt === DataType.EMAIL) return t("afvEmail", { field: name });
      if (dt === DataType.URL) return t("afvUrl", { field: name });
      if (isDate) return t("afvDate", { field: name });
      return t("afvGeneric", { field: name });

    case "invalidAdditionalFieldOption":
      return t("afvOption", { field: name });

    case "additionalFieldValueNotFinite":
      return t("afvNotFinite", { field: name });

    case "invalidAdditionalFieldValueType":
      return t("afvType", { field: name });

    case "computedFieldNotWritable":
      return t("afvComputed", { field: name });

    default:
      return t("afvGeneric", { field: name });
  }
}

/**
 * Stable i18n lookup key for a backend error code. Follows the existing
 * flat `errors.<camelCaseCode>` convention used elsewhere in
 * `messages/<locale>.json` (e.g. errors.eventQuotaExceeded). Missing
 * translations fall back to the code itself (readable-if-ugly).
 */
export function i18nKeyForAdditionalFieldError(
  code: AdditionalFieldErrorCode,
): string {
  return `errors.${code}`;
}
